'use client'
import { useState, useRef, useCallback } from 'react'
import { ObjectType } from '@/core/api/modules/document'
import { useCreateOrderMutation, useUpdateOrderMutation } from '@/core/graphql/hooks'
import { AvailabilityMeetingPlace, OrderStatus } from '@/core/graphql/types'
import { ConsultationMode } from './bookingTypes'
import { useUploadFile } from '@/shared/model/useUploadFile'
import { useGetMeetingLink } from '@/shared/api/meeting-link'
import { useBooking } from './bookingContext'
import { PatientInfoFormData } from './patientInfoForm'

export type UploadedFile = {
  filename: string
  key: string
  objectType?: string
  uploadedBy?: string
  url?: string
}

export type OrderData = {
  _id: string
  referenceId: string
  status?: string
  files?: Array<{
    filename: string
    key: string
    url?: string | null
  }> | null
  __typename?: string
}

type SubmitOrderParams = {
  comment?: string
  formSubmissionId?: string | null
  status?: OrderStatus
}

export const useOrderManagement = (meetingLinkSlug: string) => {
  const [draftOrder, isDraftingOrder, isLoadingMeetingLink] = useDraftOrder(meetingLinkSlug)
  const [submitOrder] = useSubmitOrder(meetingLinkSlug)
  const [uploadFile] = useUploadFile()

  const uploadFiles = async (files: File[], objectId: string, objectType: ObjectType = 'patient'): Promise<void> => {
    for (const file of files) {
      try {
        await uploadFile(file, objectId, objectType)
      } catch (error) {
        console.error('Error uploading file:', error)
      }
    }
  }

  return {
    draftOrder,
    isDraftingOrder,
    submitOrder,
    uploadFiles,
    isLoadingMeetingLink,
  }
}

function useDraftOrder(meetingLinkSlug: string) {
  const { dispatch, state } = useBooking()

  const [isDraftingOrder, setIsDraftingOrder] = useState(false)

  const { meetingLink, isLoadingMeetingLink } = useGetMeetingLink(meetingLinkSlug)
  const [createOrder] = useCreateOrderMutation()

  const draftOrder = useCallback(
    async (patientInfo: Pick<PatientInfoFormData, 'email' | 'firstName' | 'lastName' | 'phone'>, isLastStep = false): Promise<string | null> => {
      const meetingPlace = state.meetingPlace
      const meetingPlaceDescription = state.meetingPlaceDescription
      const speciality = state.selectedSpeciality
      const startDate = state.startDate

      // Don't create if we're already in the process of drafting an order
      if (isDraftingOrder) {
        console.log('🔍 [ORDER TRACKING] draftOrder - Order creation already in progress, skipping')
        return null
      }

      try {
        setIsDraftingOrder(true)

        const isImmediate = state.consultationMode === ConsultationMode.Immediate
        const orderStartDate = meetingPlace === AvailabilityMeetingPlace.SecondOpinion || isImmediate ? new Date().toISOString() : startDate

        console.log('🔍 [ORDER TRACKING] draftOrder - Received isLastStep:', isLastStep)

        if (!meetingLink || !meetingPlace || !meetingPlaceDescription || !speciality || !orderStartDate) {
          console.log('🔍 [ORDER TRACKING] draftOrder - Missing required data:', {
            meetingLink: !!meetingLink,
            meetingPlace: !!meetingPlace,
            speciality: !!speciality,
            orderStartDate: !!orderStartDate,
          })
          return null
        }

        const payload = {
          patientInfo,
          meetingLink: meetingLink._id,
          meetingPlace: {
            placeId: meetingPlace,
            description: meetingPlaceDescription,
            ...(state.meetingPlaceDetails || {}),
          },
          speciality,
          startDate: orderStartDate,
          status: isLastStep ? OrderStatus.Pending : OrderStatus.Draft,
          isImmediate,
          patientConsent: state.consents
            ? {
                freeChoice: state.consents.freeChoice,
                informedAboutTeleconsultation: state.consents.informedAboutTeleconsultation,
                acceptedPricing: state.consents.acceptedPricing,
                dataForResearch: state.consents.dataForResearch,
                dataForServiceImprovement: state.consents.dataForServiceImprovement,
                acceptedAt: state.consents.acceptedAt || new Date().toISOString(),
              }
            : undefined,
          companion: state.companion || undefined,
        }

        console.log('🔍 [ORDER TRACKING] draftOrder - Creating order with payload:', payload)

        const { data } = await createOrder({ variables: { payload } })

        console.log('🔍 [ORDER TRACKING] draftOrder - Draft order creation result:', data)

        if (!data) return null

        // Sync with context
        console.log('🔄 [ORDER SYNC] Syncing new order ID to context:', data.createOrder._id)
        dispatch({ type: 'SET_ORDER_ID', payload: data.createOrder._id })
        dispatch({ type: 'SET_ORDER_REFERENCE_ID', payload: data.createOrder.referenceId })

        // For immediate consultations, store the consultation ID for routing
        if (data.createOrder.consultation?._id) {
          console.log('🔄 [ORDER SYNC] Syncing consultation ID for immediate consultation:', data.createOrder.consultation._id)
          dispatch({ type: 'SET_CONSULTATION_ID', payload: data.createOrder.consultation._id })
        }

        return data.createOrder._id
      } catch (error) {
        console.error('🔍 [ORDER TRACKING] draftOrder - Error drafting order:', error)
        return null
      } finally {
        setIsDraftingOrder(false)
      }
    },
    [createOrder, dispatch, isDraftingOrder, meetingLink, state]
  )

  return [draftOrder, isDraftingOrder, isLoadingMeetingLink] as const
}

function useSubmitOrder(meetingLinkSlug: string) {
  const { dispatch, state } = useBooking()

  // Add a ref to track if an update is in progress
  const isUpdatingOrderRef = useRef(false)

  const { meetingLink } = useGetMeetingLink(meetingLinkSlug)
  const [updateOrder] = useUpdateOrderMutation()

  const submitOrder = useCallback(
    async (params: SubmitOrderParams) => {
      const meetingPlace = state.meetingPlace
      const meetingPlaceDescription = state.meetingPlaceDescription
      const orderId = state.orderId
      const patientInfo = state.patientInfo
      const speciality = state.selectedSpeciality
      const startDate = state.startDate

      // Check if we're already updating an order
      if (isUpdatingOrderRef.current) {
        console.log('🔍 [ORDER TRACKING] submitOrder - Update already in progress, skipping')
        return null
      }

      // Set the updating flag
      isUpdatingOrderRef.current = true

      try {
        const isImmediate = state.consultationMode === ConsultationMode.Immediate
        const orderStartDate = state.meetingPlace === AvailabilityMeetingPlace.SecondOpinion || isImmediate ? new Date().toISOString() : startDate

        if (!meetingLink || !meetingPlace || !meetingPlaceDescription || !orderId || !speciality || !orderStartDate) {
          return null
        }

        console.log('🔍 [ORDER TRACKING] submitOrder - Starting with order ID:', orderId)

        try {
          console.log('🔍 [ORDER TRACKING] submitOrder - Updating order:', {
            orderId,
            params,
            patientInfo,
          })

          const { data } = await updateOrder({
            variables: {
              orderId,
              input: {
                patientInfo: {
                  firstName: patientInfo.firstName,
                  lastName: patientInfo.lastName,
                  email: patientInfo.email,
                  ...(patientInfo.phone ? { phone: patientInfo.phone } : {}),
                },
                comment: params.comment,
                formSubmission: params.formSubmissionId,
                status: params.status || OrderStatus.Pending,
                meetingLink: meetingLink._id,
                meetingPlace: {
                  placeId: meetingPlace,
                  description: meetingPlaceDescription,
                  ...(state.meetingPlaceDetails || {}),
                },
                speciality,
                startDate: orderStartDate,
                patientConsent: state.consents
                  ? {
                      freeChoice: state.consents.freeChoice,
                      informedAboutTeleconsultation: state.consents.informedAboutTeleconsultation,
                      acceptedPricing: state.consents.acceptedPricing,
                      dataForResearch: state.consents.dataForResearch,
                      dataForServiceImprovement: state.consents.dataForServiceImprovement,
                      acceptedAt: state.consents.acceptedAt || new Date().toISOString(),
                    }
                  : undefined,
                companion: state.companion || undefined,
              },
            },
          })

          console.log('🔍 [ORDER TRACKING] submitOrder - Update result:', data)

          if (!data) return null

          const updatedOrder = data.updateOrder

          // Sync with context
          if (dispatch && (!state.orderId || state.orderId !== updatedOrder._id)) {
            console.log('🔄 [ORDER SYNC] Syncing updated order ID to context:', updatedOrder._id)
            dispatch({ type: 'SET_ORDER_ID', payload: updatedOrder._id })
            dispatch({ type: 'SET_ORDER_REFERENCE_ID', payload: updatedOrder.referenceId })
          }

          // If this is a completion (Pending status), mark it in the context
          if (params.status === OrderStatus.Pending && dispatch) {
            console.log('🔄 [ORDER SYNC] Order is now Pending, updating completion status')
            // We don't have a direct SET_BOOKING_COMPLETED action, but we could add one
            // For now we'll use this as a signal that the booking process has reached a completed state
            dispatch({ type: 'SET_PAYMENT_COMPLETED', payload: true })
          }
          return updatedOrder
        } catch (error) {
          console.error('❌ [ORDER TRACKING] submitOrder - Error updating order:', error)
          return null
        }
      } finally {
        // Reset the updating flag
        isUpdatingOrderRef.current = false
      }
    },
    [meetingLink, state, updateOrder, dispatch]
  )

  return [submitOrder] as const
}
